/*!
 * MONTVIELLE — collection faceting (mv-collection.js)
 *
 * The grid is server-rendered in full: every price, badge and size comes from Liquid.
 * This layer only indexes what is already printed and then shows, hides and re-orders it.
 * That is why filtering is instant, counts are always honest, and quick add never
 * costs a page load — it hands the variant straight to the cart drawer.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-mv-collection]');
  if (!root) return;

  const grid    = root.querySelector('[data-mv-grid]');
  const form    = root.querySelector('[data-mv-filters]');
  if (!grid || !form) return;

  const chipBox = root.querySelector('[data-mv-chips]');
  const result  = root.querySelector('[data-mv-result]');
  const applyN  = root.querySelector('[data-mv-applycount]');
  const badge   = root.querySelector('[data-mv-badge]');
  const empty   = root.querySelector('[data-mv-empty]');
  const sortEl  = root.querySelector('[data-mv-sort]');
  const panel   = root.querySelector('[data-mv-panel]');
  const main    = root.querySelector('[data-mv-main]');
  const tools   = root.querySelector('.mv-ctools');
  const openBtn = root.querySelector('[data-mv-open-filters]');
  const SECTION = root.dataset.mvSection;
  const PAGE    = Number(root.dataset.mvPage) || 1;

  const mobile = window.matchMedia('(max-width: 999px)');
  const html   = document.documentElement;

  let records = [];
  let groups  = [];

  /* ------------------------------------------------------------------ *
   * 1. Hydration — pull the rest of the collection in so the filters
   *    reason about every product, not just the first page.
   * ------------------------------------------------------------------ */
  async function hydrate() {
    let next = root.dataset.mvNext;
    let guard = 0;
    while (next && guard++ < 12) {
      let markup;
      try {
        const url = new URL(next, location.origin);
        url.searchParams.set('sections', SECTION);
        const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (!res.ok) return false;
        markup = (await res.json())[SECTION];
      } catch (error) {
        return false;
      }
      if (!markup) return false;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const cards = doc.querySelectorAll('[data-mv-card]');
      if (!cards.length) return false;
      grid.append(...cards);
      const incoming = doc.querySelector('[data-mv-collection]');
      next = incoming ? incoming.dataset.mvNext || '' : '';
    }
    if (next) return false;
    root.dataset.mvNext = '';
    const pager = root.querySelector('[data-mv-pager]');
    if (pager) pager.remove();
    return true;
  }

  /* ------------------------------------------------------------------ *
   * 2. Index — every data-f-* attribute on a card becomes a facet set.
   * ------------------------------------------------------------------ */
  function index() {
    records = Array.from(grid.querySelectorAll('[data-mv-card]')).map((el, pos) => {
      const f = {};
      for (const attr of el.attributes) {
        if (!attr.name.startsWith('data-f-')) continue;
        const values = attr.value.split('|').filter(Boolean);
        if (values.length) f[attr.name.slice(7)] = new Set(values);
      }
      return { el, f, pos, price: Number(el.dataset.price) || 0, warmth: Number(el.dataset.warmth) || 0 };
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Reconcile — the rail ships the full vocabulary; drop anything this
   *    collection does not actually carry, and any group left with one choice.
   * ------------------------------------------------------------------ */

  /* Groups built from the products themselves arrive in the order Liquid met them,
     which for ski lengths reads 156, 163, 170, 177, 164, 171… Sort them the way the
     shop floor does: apparel by size, everything numeric by number, kids after adults. */
  const CURATED = new Set(['layer', 'warmth', 'sport', 'gender', 'price']);
  const WORDS = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', 'xxxl', '3xl', '4xl'];

  function sizeRank(label) {
    const text = (label || '').trim();
    const kids = /^(kids?|youth|jr)\b/i.test(text);
    const rest = text.replace(/^(kids?|youth|jr)\s*/i, '');
    const lead = (rest.match(/^([mwu])\s+/i) || [])[1] || '';
    const core = lead ? rest.slice(lead.length).trim() : rest;
    const num = parseFloat(core.replace(',', '.'));
    let bucket = 3;
    let value = 0;
    if (/^[\d.]/.test(core) && !Number.isNaN(num)) { bucket = 1; value = num; }
    else {
      const i = WORDS.indexOf(core.toLowerCase().replace(/\s+/g, ''));
      if (i > -1) { bucket = 0; value = i; }
    }
    return [kids ? 1 : 0, lead.toUpperCase().charCodeAt(0) || 0, bucket, value];
  }

  function sortGroup(g) {
    if (CURATED.has(g.key)) return;                       /* authored order is deliberate */
    const body = g.el.querySelector('.mv-facet__body');
    if (!body) return;
    const byLabel = g.key === 'type' || g.key === 'line';
    const keyed = g.opts.map((o) => ({
      o,
      text: o.label ? o.label.textContent.trim() : o.v,
      rank: byLabel ? null : sizeRank(o.label ? o.label.textContent : o.v)
    }));
    keyed.sort((a, b) => {
      if (!byLabel) {
        for (let i = 0; i < a.rank.length; i += 1) {
          if (a.rank[i] !== b.rank[i]) return a.rank[i] - b.rank[i];
        }
      }
      return a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' });
    });
    keyed.forEach((k) => body.append(k.o.el));
    g.opts = keyed.map((k) => k.o);
  }

  function reconcile() {
    groups = Array.from(form.querySelectorAll('[data-mv-group]')).map((el) => ({
      key: el.dataset.mvGroup,
      el,
      opts: Array.from(el.querySelectorAll('[data-mv-opt]')).map((o) => ({
        el: o,
        v: o.dataset.mvOpt,
        box: o.querySelector('input'),
        count: o.querySelector('[data-mv-count]'),
        label: o.querySelector('.mv-opt__text')
      }))
    }));

    for (const g of groups) {
      sortGroup(g);
      let live = 0;
      for (const o of g.opts) {
        const exists = records.some((r) => r.f[g.key] && r.f[g.key].has(o.v));
        o.el.hidden = !exists;
        if (!exists) o.box.checked = false;
        else live += 1;
      }
      g.el.hidden = live < 2;
      if (g.el.hidden) g.opts.forEach((o) => { o.box.checked = false; });
    }
  }

  /* ------------------------------------------------------------------ *
   * 4. Apply
   * ------------------------------------------------------------------ */
  const SORTS = {
    'featured':     (a, b) => a.pos - b.pos,
    'price-asc':    (a, b) => a.price - b.price || a.pos - b.pos,
    'price-desc':   (a, b) => b.price - a.price || a.pos - b.pos,
    'warmth-desc':  (a, b) => b.warmth - a.warmth || a.price - b.price || a.pos - b.pos
  };

  function chosen() {
    const sel = {};
    for (const g of groups) {
      sel[g.key] = g.el.hidden ? [] : g.opts.filter((o) => !o.el.hidden && o.box.checked).map((o) => o.v);
    }
    return sel;
  }

  function apply() {
    const sel = chosen();

    /* A product passes a group when it carries any of that group's picked values.
       Groups combine with AND, values inside a group with OR — the combination
       shoppers actually expect. */
    const passes = (rec, skip) => groups.every((g) => {
      if (g.key === skip || !sel[g.key].length) return true;
      const have = rec.f[g.key];
      return !!have && sel[g.key].some((v) => have.has(v));
    });

    const matched = records.filter((r) => passes(r, null));
    const hit = new Set(matched.map((r) => r.el));
    for (const r of records) r.el.hidden = !hit.has(r.el);

    const sorter = SORTS[sortEl && sortEl.value] || SORTS.featured;
    matched.slice().sort(sorter).forEach((r, i) => { r.el.style.order = String(i); });

    /* Counts are computed with this group's own picks removed, so a facet never
       reports zero for a choice the visitor can plainly see is available. */
    let active = 0;
    for (const g of groups) {
      if (g.el.hidden) continue;
      const pool = records.filter((r) => passes(r, g.key));
      for (const o of g.opts) {
        if (o.el.hidden) continue;
        let n = 0;
        for (const r of pool) if (r.f[g.key] && r.f[g.key].has(o.v)) n += 1;
        if (o.count) o.count.textContent = String(n);
        const dead = n === 0 && !o.box.checked;
        o.el.classList.toggle('is-empty', dead);
        o.box.disabled = dead;
      }
      const picked = sel[g.key].length;
      active += picked;
      const tally = g.el.querySelector('[data-mv-tally]');
      if (tally) { tally.textContent = picked ? String(picked) : ''; tally.hidden = !picked; }
    }

    const noun = matched.length === 1 ? 'piece' : 'pieces';
    if (result) result.textContent = `${matched.length} ${noun}`;
    if (applyN) applyN.textContent = `${matched.length} ${noun}`;
    if (badge) { badge.textContent = active ? String(active) : ''; badge.hidden = !active; }
    if (empty) empty.hidden = matched.length > 0;
    grid.hidden = matched.length === 0;

    paintChips(sel, active);
    writeUrl(sel);
  }

  function paintChips(sel, active) {
    if (!chipBox) return;
    chipBox.textContent = '';
    if (!active) { chipBox.hidden = true; return; }

    for (const g of groups) {
      if (g.el.hidden) continue;
      for (const o of g.opts) {
        if (!o.box.checked || o.el.hidden) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mv-chip';
        button.textContent = o.label ? o.label.textContent.trim() : o.v;
        button.setAttribute('aria-label', `Remove filter ${button.textContent}`);
        button.addEventListener('click', () => { o.box.checked = false; apply(); });
        chipBox.append(button);
      }
    }
    if (active > 1) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'mv-chip mv-chip--clear';
      clear.textContent = 'Clear all';
      clear.addEventListener('click', clearAll);
      chipBox.append(clear);
    }
    chipBox.hidden = false;
  }

  function writeUrl(sel) {
    const q = new URLSearchParams();
    if (PAGE > 1) q.set('page', String(PAGE));
    for (const g of groups) if (sel[g.key].length) q.set(g.key, sel[g.key].join(','));
    if (sortEl && sortEl.value && sortEl.value !== 'featured') q.set('sort', sortEl.value);
    const qs = q.toString();
    history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
  }

  function readUrl() {
    const q = new URLSearchParams(location.search);
    for (const g of groups) {
      const raw = q.get(g.key);
      if (!raw) continue;
      const want = new Set(raw.split(','));
      for (const o of g.opts) if (want.has(o.v) && !o.el.hidden) o.box.checked = true;
    }
    const sort = q.get('sort');
    if (sortEl && sort && SORTS[sort]) sortEl.value = sort;
  }

  function clearAll() {
    for (const g of groups) for (const o of g.opts) o.box.checked = false;
    apply();
  }

  form.addEventListener('change', apply);
  if (sortEl) sortEl.addEventListener('change', apply);
  root.querySelectorAll('[data-mv-clear]').forEach((b) => b.addEventListener('click', clearAll));

  /* ------------------------------------------------------------------ *
   * 5. The mobile filter panel
   * ------------------------------------------------------------------ */
  let lockedHere = false;

  function setPanel(on) {
    if (!panel || !mobile.matches) return;
    root.classList.toggle('is-filtering', on);
    if (openBtn) openBtn.setAttribute('aria-expanded', String(on));
    if (main) main.inert = on;
    if (tools) tools.inert = on;

    if (on && !html.classList.contains('mv-lock')) { html.classList.add('mv-lock'); lockedHere = true; }
    else if (!on && lockedHere) { html.classList.remove('mv-lock'); lockedHere = false; }

    if (window.MV && window.MV.header) {
      if (on) window.MV.header.lock('filters'); else window.MV.header.unlock('filters');
    }

    if (on) {
      const close = panel.querySelector('[data-mv-close-filters]');
      if (close) close.focus();
    } else if (openBtn) {
      openBtn.focus();
    }
  }

  if (openBtn) openBtn.addEventListener('click', () => setPanel(true));
  root.querySelectorAll('[data-mv-close-filters]').forEach((b) => b.addEventListener('click', () => setPanel(false)));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('is-filtering')) setPanel(false);
  });
  mobile.addEventListener('change', () => { if (!mobile.matches) { root.classList.remove('is-filtering'); if (main) main.inert = false; if (tools) tools.inert = false; if (lockedHere) { html.classList.remove('mv-lock'); lockedHere = false; } } });

  /* ------------------------------------------------------------------ *
   * 6. Quick add — the drawer does the rest
   * ------------------------------------------------------------------ */
  grid.addEventListener('click', async (e) => {
    const toggle = e.target.closest('[data-mv-qa-open]');
    if (toggle) {
      e.preventDefault();
      const card = toggle.closest('[data-mv-card]');
      const open = card.getAttribute('data-sizes') !== 'open';
      grid.querySelectorAll('[data-mv-card][data-sizes="open"]').forEach((c) => {
        c.removeAttribute('data-sizes');
        const t = c.querySelector('[data-mv-qa-open]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      if (open) card.setAttribute('data-sizes', 'open');
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const button = e.target.closest('[data-mv-add]');
    if (!button || button.disabled) return;
    e.preventDefault();

    const card = button.closest('[data-mv-card]');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const id = button.dataset.mvAdd;
      if (window.MV && window.MV.cart && window.MV.cart.add) await window.MV.cart.add(id, 1);
      else document.dispatchEvent(new CustomEvent('mv:cart:add', { detail: { id, quantity: 1 } }));
      if (card) {
        /* Name the size back at them. "In your bag · M" is the difference between believing
           the click landed and clicking again. The wording itself comes from the markup —
           the script only fills the slot. */
        card.removeAttribute('data-sizes');
        const opener = card.querySelector('[data-mv-qa-open]');
        if (opener) opener.setAttribute('aria-expanded', 'false');
        const done = card.querySelector('.mv-card__done');
        const said = card.querySelector('[data-mv-done-text]');
        const word = (done && done.dataset.mvDoneWord) || '';
        const label = button.dataset.mvLabel;
        if (said) said.textContent = label ? `${word} · ${label}` : word;
        const failed = card.querySelector('[data-mv-fail]');
        if (failed) failed.hidden = true;
        card.setAttribute('data-added', '');
        clearTimeout(card.mvAddedTimer);
        card.mvAddedTimer = setTimeout(() => card.removeAttribute('data-added'), 2400);
      }
    } catch (error) {
      /* Shopify's own sentence — "All 1 of this are in your cart", or the real reason the
         add was refused. Guessing at it (and striking the size out) told a shopper who
         already held the last one that it had sold out. */
      const failed = card && card.querySelector('[data-mv-fail]');
      if (failed) {
        failed.textContent = (error && error.message) || 'That could not be added.';
        failed.hidden = false;
        clearTimeout(card.mvFailTimer);
        card.mvFailTimer = setTimeout(() => { failed.hidden = true; }, 6000);
      }
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  });

  /* A click anywhere else closes an open size row. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-mv-card]')) return;
    grid.querySelectorAll('[data-mv-card][data-sizes="open"]').forEach((c) => {
      c.removeAttribute('data-sizes');
      const t = c.querySelector('[data-mv-qa-open]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  });

  /* ------------------------------------------------------------------ *
   * 7. Boot
   * ------------------------------------------------------------------ */
  (async () => {
    if (PAGE === 1 && root.dataset.mvNext) await hydrate();
    index();
    reconcile();
    readUrl();
    apply();
    root.classList.add('is-ready');
  })();
})();
